import React, { useEffect, useState, useMemo, useRef } from 'react';
import Header from '../components/layout/Header';
import AddProviderModal from '../components/common/AddProviderModal';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import {
  getIntakes, createIntake, updateIntake, deleteIntake, convertIntake,
  getUsers, getCustomLists, getCareProviders, createCareProvider
} from '../utils/api';
import { formatDate, formatDateTime } from '../utils/helpers';
import {
  Plus, Search, Trash2, Pencil, UserPlus, ArrowRight,
  Phone, Mail, Clock, AlertTriangle, CheckCircle, XCircle,
  Calendar, ChevronDown, Filter, Briefcase, User, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  new:                    { label: 'New',            color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  contacted:              { label: 'Contacted',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
  consultation_scheduled: { label: 'Consult Sched.', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  consultation_done:      { label: 'Consult Done',   color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  converted:              { label: 'Converted',      color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  declined:               { label: 'Declined',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  no_response:            { label: 'No Response',    color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: '#64748b' },
  normal: { label: 'Normal', color: '#3b82f6' },
  high:   { label: 'High',   color: '#f97316' },
  urgent: { label: 'Urgent', color: '#ef4444' },
};

const SOURCES = ['Phone', 'Walk-In', 'Website', 'Referral', 'Advertisement', 'Social Media', 'Other'];

const EMPTY_FORM = {
  first_name: '', last_name: '', phone: '', phone_alt: '', email: '',
  address: '', city: '', state: '', zip: '', date_of_birth: '', language: [],
  date_of_incident: '', incident_description: '', body_parts: [],
  mechanism_of_injury: '', employer_name: '',
  case_type: '', referred_by: '', source: '',
  status: 'new', priority: 'normal',
  assigned_to: '', screener: '',
  follow_up_date: '', consult_date: '', notes: '',
  sol_date: '', sol_years: 2,
};

// SOL calculation helper
function calcSOL(doi, years = 2) {
  if (!doi) return { sol_date: '', badge: null };
  const d = new Date(doi + 'T00:00:00');
  d.setFullYear(d.getFullYear() + years);
  const solStr = d.toISOString().split('T')[0];
  const today = new Date(); today.setHours(0,0,0,0);
  const diffMs = d.getTime() - today.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  let badge;
  if (daysLeft < 0) badge = { color: '#7c3aed', bg: 'rgba(124,58,237,0.12)', label: 'PAST DUE', days: daysLeft };
  else if (daysLeft <= 30) badge = { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'CRITICAL', days: daysLeft };
  else if (daysLeft <= 90) badge = { color: '#f97316', bg: 'rgba(249,115,22,0.12)', label: 'URGENT', days: daysLeft };
  else if (daysLeft <= 180) badge = { color: '#eab308', bg: 'rgba(234,179,8,0.12)', label: 'WATCH', days: daysLeft };
  else badge = { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'SAFE', days: daysLeft };
  return { sol_date: solStr, badge };
}

// Get SOL badge from an intake record (for list view)
function getSOLBadgeFromIntake(intake) {
  const doi = intake.date_of_incident;
  if (!doi) return null;
  const years = intake.sol_years ?? 2;
  return calcSOL(typeof doi === 'string' ? doi.substring(0, 10) : new Date(doi).toISOString().split('T')[0], years).badge;
}

export default function IntakePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const [users, setUsers] = useState([]);
  const [typeOptions, setTypeOptions] = useState(['Workers Compensation', 'Personal Injury', 'Slip and Fall', 'Motor Vehicle Accident', 'Death on the Job', 'Industrial Accident', 'Other']);
  const [bodyPartOpts, setBodyPartOpts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [refSearch, setRefSearch] = useState('');
  const [showRefDropdown, setShowRefDropdown] = useState(false);
  const [addingProvider,    setAddingProvider]    = useState(false);
  const [showAddProvider,   setShowAddProvider]   = useState(false);
  const [addProviderName,   setAddProviderName]   = useState('');
  const [specialties,       setSpecialties]       = useState([]);
  const [providerTypes,     setProviderTypes]     = useState([]);
  const refDropdownRef = useRef(null);

  useEffect(() => { loadIntakes(); loadMeta(); }, []);

  // Close referred-by dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (refDropdownRef.current && !refDropdownRef.current.contains(e.target)) setShowRefDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadIntakes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const data = await getIntakes(params);
      setIntakes(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load intakes'); setIntakes([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadIntakes(); }, [statusFilter, priorityFilter]);

  const loadMeta = async () => {
    try {
      const [u, lists, provs] = await Promise.all([getUsers(), getCustomLists(), getCareProviders().catch(() => [])]);
      setUsers(Array.isArray(u) ? u : (u?.users || []));
      if (lists?.legal_type_options?.length)    setTypeOptions(lists.legal_type_options);
      if (lists?.body_parts?.length)             setBodyPartOpts(lists.body_parts);
      if (lists?.provider_type_options?.length)  setProviderTypes(lists.provider_type_options);
      if (lists?.specialty_options?.length)      setSpecialties(lists.specialty_options);
      setProviders(Array.isArray(provs) ? provs : (provs?.providers || []));
    } catch {}
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return intakes;
    return intakes.filter(i => {
      const name = `${i.first_name || ''} ${i.last_name || ''}`.toLowerCase();
      const phone = (i.phone || '').toLowerCase();
      const email = (i.email || '').toLowerCase();
      return name.includes(s) || phone.includes(s) || email.includes(s);
    });
  }, [intakes, search]);

  // Stats
  const stats = useMemo(() => {
    const s = { total: intakes.length, new: 0, contacted: 0, consult: 0, converted: 0 };
    intakes.forEach(i => {
      if (i.status === 'new') s.new++;
      else if (i.status === 'contacted') s.contacted++;
      else if (i.status === 'consultation_scheduled' || i.status === 'consultation_done') s.consult++;
      else if (i.status === 'converted') s.converted++;
    });
    return s;
  }, [intakes]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setRefSearch('');
    setShowRefDropdown(false);
    setShowCreate(true);
  };

  const openEdit = (intake) => {
    setForm({
      first_name: intake.first_name || '',
      last_name: intake.last_name || '',
      phone: intake.phone || '',
      phone_alt: intake.phone_alt || '',
      email: intake.email || '',
      address: intake.address || '',
      city: intake.city || '',
      state: intake.state || '',
      zip: intake.zip || '',
      date_of_birth: intake.date_of_birth ? intake.date_of_birth.substring(0, 10) : '',
      language: intake.language || [],
      date_of_incident: intake.date_of_incident ? intake.date_of_incident.substring(0, 10) : '',
      incident_description: intake.incident_description || '',
      body_parts: intake.body_parts || [],
      mechanism_of_injury: intake.mechanism_of_injury || '',
      employer_name: intake.employer_name || '',
      case_type: intake.case_type || '',
      referred_by: intake.referred_by || '',
      source: intake.source || '',
      status: intake.status || 'new',
      priority: intake.priority || 'normal',
      assigned_to: intake.assigned_to?._id || intake.assigned_to || '',
      screener: intake.screener?._id || intake.screener || '',
      follow_up_date: intake.follow_up_date ? intake.follow_up_date.substring(0, 10) : '',
      consult_date: intake.consult_date ? intake.consult_date.substring(0, 10) : '',
      notes: intake.notes || '',
      sol_date: intake.sol_date ? intake.sol_date.substring(0, 10) : '',
      sol_years: intake.sol_years ?? 2,
    });
    setRefSearch('');
    setShowRefDropdown(false);
    setShowEdit(intake);
  };

  const handleSave = async (forceSkipDup = false) => {
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      // Auto-calculate SOL date before saving
      const saveData = { ...form };
      if (saveData.date_of_incident) {
        const sol = calcSOL(saveData.date_of_incident, saveData.sol_years || 2);
        saveData.sol_date = sol.sol_date;
      }
      if (forceSkipDup) saveData.skip_duplicate_check = true;
      if (showEdit) {
        await updateIntake(showEdit._id, saveData);
        toast.success('Intake updated');
        setShowEdit(null);
      } else {
        await createIntake(saveData);
        toast.success('Intake created');
        setShowCreate(false);
      }
      loadIntakes();
    } catch (err) {
      if (!showEdit && err.status === 409 && err.data?.error === 'duplicate_found') {
        const dups = err.data.duplicates || [];
        const matchLabels = { ssn: 'SSN Match', dob_name: 'DOB + Name', doi_name: 'DOI + Name' };
        setConfirmAction({
          title: 'Potential Duplicates Found',
          message: `${dups.length} existing case${dups.length > 1 ? 's' : ''} match${dups.length === 1 ? 'es' : ''} this intake:\n\n${dups.map(d =>
            `${d.case_number || 'No #'} - ${d.client_name} (${d.status}) [${matchLabels[d.match_type] || d.match_type}]`
          ).join('\n')}\n\nCreate intake anyway?`,
          confirmLabel: 'Create Anyway',
          danger: true,
          onConfirm: () => handleSave(true),
        });
      } else {
        toast.error(err?.message || 'Failed to save');
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (intake, e) => {
    e?.stopPropagation();
    setConfirmAction({
      title: 'Delete Intake',
      message: `Delete intake for ${intake.first_name} ${intake.last_name}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => { try { await deleteIntake(intake._id); toast.success('Intake deleted'); loadIntakes(); } catch { toast.error('Failed to delete'); } },
    });
  };

  const handleConvert = async (intake, forceSkipDup = false) => {
    setConfirmAction({
      title: 'Convert to Case',
      message: `Convert intake for ${intake.first_name} ${intake.last_name} to a new case? This will create a client and case record.`,
      confirmLabel: 'Convert',
      danger: false,
      onConfirm: async () => {
        setConverting(intake._id);
        try {
          const result = await convertIntake(intake._id, { skip_duplicate_check: forceSkipDup });
          toast.success(`Case ${result.case?.case_number} created!`);
          loadIntakes();
          if (result.case?._id) {
            setTimeout(() => navigate(`/cases/${result.case._id}`), 500);
          }
        } catch (err) {
          if (err.status === 409 && err.data?.error === 'duplicate_found') {
            // Show duplicate warning with option to proceed
            const dups = err.data.duplicates || [];
            const matchLabels = { ssn: 'SSN Match', dob_name: 'DOB + Name', doi_name: 'DOI + Name' };
            setConfirmAction({
              title: 'Potential Duplicates Found',
              message: `${dups.length} existing case${dups.length > 1 ? 's' : ''} match${dups.length === 1 ? 'es' : ''} this intake:\n\n${dups.map(d =>
                `${d.case_number || 'No #'} - ${d.client_name} (${d.status}) [${matchLabels[d.match_type] || d.match_type}]`
              ).join('\n')}\n\nConvert anyway?`,
              confirmLabel: 'Convert Anyway',
              danger: true,
              onConfirm: async () => {
                setConverting(intake._id);
                try {
                  const result = await convertIntake(intake._id, { skip_duplicate_check: true });
                  toast.success(`Case ${result.case?.case_number} created!`);
                  loadIntakes();
                  if (result.case?._id) setTimeout(() => navigate(`/cases/${result.case._id}`), 500);
                } catch (e) { toast.error(e?.message || 'Failed to convert'); }
                finally { setConverting(null); }
              },
            });
          } else {
            toast.error(err?.message || err?.data?.error || 'Failed to convert');
          }
        }
        finally { setConverting(null); }
      },
    });
  };

  const handleQuickStatus = async (intake, newStatus, e) => {
    e?.stopPropagation();
    try {
      await updateIntake(intake._id, { status: newStatus });
      toast.success(`Status → ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      loadIntakes();
    } catch { toast.error('Failed to update status'); }
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '0.73rem', fontWeight: 700,
        background: cfg.bg, color: cfg.color,
      }}>
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: cfg.color }} />
        {cfg.label}
      </span>
    );
  };

  const PriorityDot = ({ priority }) => {
    const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700,
        background: `${cfg.color}18`, color: cfg.color,
      }}>
        {cfg.label}
      </span>
    );
  };

  // ── FORM MODAL CONTENT ──
  const formContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Contact Info */}
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--zeus-gold)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--zeus-border)', paddingBottom: '6px' }}>
        Contact Information
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name *</label>
          <input className="form-input" value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name *</label>
          <input className="form-input" value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input className="form-input" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 555-5555" />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">DOB</label>
          <input className="form-input" type="date" value={form.date_of_birth} onChange={(e) => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Alt Phone</label>
          <input className="form-input" value={form.phone_alt} onChange={(e) => setForm(f => ({ ...f, phone_alt: e.target.value }))} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Address</label>
          <AddressAutocomplete
              value={form.address}
              onChange={(v) => setForm(f => ({ ...f, address: v }))}
              onPlaceSelect={({ street, city, state, zip }) => setForm(f => ({
                ...f,
                address: street,
                city:    city  || f.city,
                state:   state || f.state,
                zip:     zip   || f.zip,
              }))}
            />
        </div>
        <div className="form-group">
          <label className="form-label">City</label>
          <input className="form-input" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
        </div>
      </div>
      <div className="form-row-3">
        <div className="form-group">
          <label className="form-label">State</label>
          <input className="form-input" value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))} maxLength={2} />
        </div>
        <div className="form-group">
          <label className="form-label">ZIP</label>
          <input className="form-input" value={form.zip} onChange={(e) => setForm(f => ({ ...f, zip: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Source</label>
          <select className="form-select" value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}>
            <option value="">— Source —</option>
            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Incident Info */}
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--zeus-gold)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--zeus-border)', paddingBottom: '6px', marginTop: '4px' }}>
        Incident Details
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Case Type</label>
          <select className="form-select" value={form.case_type} onChange={(e) => setForm(f => ({ ...f, case_type: e.target.value }))}>
            <option value="">— Select Type —</option>
            {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">DOI</label>
          <input className="form-input" type="date" value={form.date_of_incident} onChange={(e) => {
            const doi = e.target.value;
            const sol = calcSOL(doi, form.sol_years || 2);
            setForm(f => ({ ...f, date_of_incident: doi, sol_date: sol.sol_date }));
          }} />
          {/* SOL Status Badge */}
          {form.date_of_incident && (() => {
            const sol = calcSOL(form.date_of_incident, form.sol_years || 2);
            if (!sol.badge) return null;
            const b = sol.badge;
            return (
              <div style={{ marginTop:'4px', display:'flex', alignItems:'center', gap:'6px' }}>
                <span style={{
                  display:'inline-flex', alignItems:'center', gap:'4px',
                  padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700,
                  background: b.bg, color: b.color,
                }}>{b.label} — {b.days < 0 ? `${Math.abs(b.days)}d overdue` : `${b.days}d left`}</span>
                <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>SOL: {sol.sol_date ? (() => { const [y,m,d] = sol.sol_date.split('-'); return `${m}/${d}/${y}`; })() : ''}</span>
              </div>
            );
          })()}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Incident Description</label>
        <textarea className="form-textarea" rows={2} value={form.incident_description} onChange={(e) => setForm(f => ({ ...f, incident_description: e.target.value }))} placeholder="Brief description of the incident..." />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Employer</label>
          <input className="form-input" value={form.employer_name} onChange={(e) => setForm(f => ({ ...f, employer_name: e.target.value }))} />
        </div>
        <div className="form-group" ref={refDropdownRef}>
          <label className="form-label">Referred By</label>
          {!showRefDropdown ? (
            /* Collapsed state - looks like a normal select */
            <div
              onClick={() => setShowRefDropdown(true)}
              className="form-input"
              style={{
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
                minHeight:'34px', padding:'6px 10px',
              }}
            >
              <span style={{ color: form.referred_by ? 'inherit' : '#9ca3af', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {form.referred_by || 'Select provider...'}
              </span>
              {form.referred_by ? (
                <X size={13} style={{ color:'#9ca3af', flexShrink:0, cursor:'pointer' }} onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, referred_by: '' })); }} />
              ) : (
                <ChevronDown size={13} style={{ color:'#9ca3af', flexShrink:0 }} />
              )}
            </div>
          ) : (
            /* Expanded state - inline search + list */
            <div style={{
              border:'1px solid #d4a853', borderRadius:'8px', background:'#ffffff',
              overflow:'hidden',
            }}>
              {/* Search input */}
              <div style={{ padding:'6px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', gap:'6px' }}>
                <Search size={14} style={{ color:'#9ca3af', flexShrink:0 }} />
                <input
                  type="text"
                  style={{
                    flex:1, border:'none', outline:'none', fontSize:'0.85rem', padding:'4px 0',
                    background:'transparent', color:'#1f2937',
                  }}
                  placeholder="Search providers..."
                  value={refSearch}
                  onChange={(e) => setRefSearch(e.target.value)}
                  autoFocus
                />
                <button type="button" onClick={() => { setShowRefDropdown(false); setRefSearch(''); }}
                  style={{ background:'none', border:'none', cursor:'pointer', padding:'2px', color:'#9ca3af' }}>
                  <X size={14} />
                </button>
              </div>
              {/* Provider list */}
              <div style={{ maxHeight:'160px', overflowY:'auto' }}>
                {providers
                  .filter(p => {
                    const q = refSearch.toLowerCase();
                    if (!q) return true;
                    return p.name.toLowerCase().includes(q) || (p.provider_type || '').toLowerCase().includes(q) || (p.specialty || '').toLowerCase().includes(q);
                  })
                  .slice(0, 30)
                  .map(p => (
                    <div key={p._id} onClick={() => { setForm(f => ({ ...f, referred_by: p.name })); setShowRefDropdown(false); setRefSearch(''); }}
                      style={{
                        padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem',
                        color:'#1f2937', borderBottom:'1px solid #f3f4f6',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#fef3c7'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#ffffff'}
                    >
                      <div style={{ fontWeight:500 }}>{p.name}</div>
                      {(p.provider_type || p.specialty) && (
                        <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:'1px' }}>
                          {[p.provider_type, p.specialty].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  ))
                }
                {providers.filter(p => { const q = refSearch.toLowerCase(); return !q || p.name.toLowerCase().includes(q); }).length === 0 && !refSearch && (
                  <div style={{ padding:'12px', fontSize:'0.82rem', color:'#9ca3af', textAlign:'center' }}>No providers in network yet</div>
                )}
                {providers.filter(p => { const q = refSearch.toLowerCase(); return !q || p.name.toLowerCase().includes(q); }).length === 0 && refSearch && (
                  <div style={{ padding:'12px', fontSize:'0.82rem', color:'#9ca3af', textAlign:'center' }}>No match for "{refSearch}"</div>
                )}
              </div>
              {/* Add new button */}
              <div style={{ borderTop:'1px solid #e5e7eb', padding:'6px' }}>
                {refSearch.trim() ? (
                  <button
                    type="button"
                    style={{
                      width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'4px',
                      padding:'8px 10px', fontSize:'0.8rem', fontWeight:600,
                      color:'#92400e', background:'#fef3c7', border:'1px solid #fde68a',
                      borderRadius:'6px', cursor:'pointer',
                    }}
                    disabled={addingProvider}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setAddProviderName(refSearch.trim());
                      setShowRefDropdown(false);
                      setShowAddProvider(true);
                    }}
                  >
                    <Plus size={12} /> Add "{refSearch.trim()}" as new provider
                  </button>
                ) : (
                  <div style={{ padding:'4px 8px', fontSize:'0.72rem', color:'#9ca3af', textAlign:'center' }}>Type a name above to add new</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Workflow */}
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--zeus-gold)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--zeus-border)', paddingBottom: '6px', marginTop: '4px' }}>
        Workflow & Assignment
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select className="form-select" value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Assigned To</label>
          <select className="form-select" value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Screener</label>
          <select className="form-select" value={form.screener} onChange={(e) => setForm(f => ({ ...f, screener: e.target.value }))}>
            <option value="">— None —</option>
            {users.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Follow-Up Date</label>
          <input className="form-input" type="date" value={form.follow_up_date} onChange={(e) => setForm(f => ({ ...f, follow_up_date: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Consultation Date</label>
          <input className="form-input" type="date" value={form.consult_date} onChange={(e) => setForm(f => ({ ...f, consult_date: e.target.value }))} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea className="form-textarea" rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes about this intake..." />
      </div>
    </div>
  );

  return (
    <>
      <Header title="Client Intake">
        <button className="btn btn-primary" onClick={openCreate}>
          <UserPlus size={16} /> New Intake
        </button>
      </Header>

      <div className="app-content">
        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Total Intakes', value: stats.total, color: 'var(--text-primary)' },
            { label: 'New', value: stats.new, color: '#3b82f6' },
            { label: 'In Progress', value: stats.contacted + stats.consult, color: '#f59e0b' },
            { label: 'Converted', value: stats.converted, color: '#22c55e' },
          ].map((s, i) => (
            <div key={i} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{s.label}</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'var(--font-display)', color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid var(--zeus-border)', borderRadius: '12px', background: 'var(--zeus-navy-light)', flex: 1, maxWidth: '340px' }}>
            <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input className="form-input" style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, fontSize: '0.88rem' }}
              placeholder="Search name, phone, email..."
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 'auto', padding: '8px 12px', fontSize: '0.85rem' }}
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="form-select" style={{ width: 'auto', padding: '8px 12px', fontSize: '0.85rem' }}
            value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{filtered.length} intakes</span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <UserPlus />
            <h3>No intakes yet</h3>
            <p>Add new client intakes to track prospective cases through the screening process.</p>
            <button className="btn btn-primary" onClick={openCreate}><Plus size={14} /> New Intake</button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>DOI</th>
                  <th>SOL Status</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Assigned</th>
                  <th>Follow-Up</th>
                  <th>Age</th>
                  <th style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => {
                  const age = daysSince(i.createdAt);
                  const isOverdue = i.follow_up_date && new Date(i.follow_up_date) < new Date() && i.status !== 'converted' && i.status !== 'declined';
                  return (
                    <tr key={i._id}
                      onClick={() => openEdit(i)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--zeus-gold-dim)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {i.first_name} {i.last_name}
                        {i.source && <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>{i.source}</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {i.phone && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)' }}><Phone size={11} /> {i.phone}</div>}
                        {i.email && <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '1px' }}><Mail size={11} /> {i.email}</div>}
                        {!i.phone && !i.email && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td>
                        {i.case_type ? (
                          <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'var(--zeus-navy-light)', color: 'var(--text-secondary)', fontSize: '0.76rem' }}>
                            {i.case_type}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {i.date_of_incident ? formatDate(i.date_of_incident) : '—'}
                      </td>
                      <td>
                        {(() => {
                          const sol = getSOLBadgeFromIntake(i);
                          if (!sol) return '—';
                          return (
                            <span style={{
                              display:'inline-flex', alignItems:'center', gap:'3px',
                              padding:'2px 8px', borderRadius:'10px', fontSize:'0.68rem', fontWeight:700,
                              background: sol.bg, color: sol.color, whiteSpace:'nowrap',
                            }}>{sol.label} {sol.days < 0 ? `${Math.abs(sol.days)}d` : `${sol.days}d`}</span>
                          );
                        })()}
                      </td>
                      <td><StatusBadge status={i.status} /></td>
                      <td><PriorityDot priority={i.priority} /></td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {i.assigned_to ? `${i.assigned_to.first_name || ''} ${i.assigned_to.last_name || ''}`.trim() : '—'}
                      </td>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {i.follow_up_date ? (
                          <span style={{ color: isOverdue ? '#ef4444' : 'var(--text-secondary)', fontWeight: isOverdue ? 700 : 400, display: 'flex', alignItems: 'center', gap: '3px' }}>
                            {isOverdue && <AlertTriangle size={12} />}
                            {formatDate(i.follow_up_date)}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: '0.78rem', color: age > 7 ? '#f97316' : 'var(--text-muted)', fontWeight: age > 7 ? 600 : 400 }}>
                        {age != null ? `${age}d` : '—'}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {i.status !== 'converted' && i.status !== 'declined' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => handleConvert(i)}
                              disabled={converting === i._id}
                              title="Convert to Case"
                              style={{ color: '#22c55e', padding: '4px 6px' }}>
                              {converting === i._id ? <div className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }} /> : <Briefcase size={13} />}
                            </button>
                          )}
                          {i.status === 'converted' && i.converted_case && (
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/cases/${i.converted_case._id || i.converted_case}`)}
                              title="View Case" style={{ color: 'var(--zeus-gold)', padding: '4px 6px' }}>
                              <ArrowRight size={13} />
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={(e) => handleDelete(i, e)}
                            title="Delete" style={{ color: 'var(--status-red)', padding: '4px 6px' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <Modal title="New Client Intake" onClose={() => setShowCreate(false)} wide>
          <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 2px' }}>
            {formContent}
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Creating...' : 'Create Intake'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <Modal title={`Edit: ${showEdit.first_name} ${showEdit.last_name}`} onClose={() => setShowEdit(null)} wide>
          <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 2px' }}>
            {formContent}
          </div>
          <div className="modal-footer">
            {showEdit.status !== 'converted' && showEdit.status !== 'declined' && (
              <button className="btn btn-secondary" onClick={() => { setShowEdit(null); handleConvert(showEdit); }}
                disabled={converting === showEdit._id} style={{ marginRight: 'auto' }}>
                <Briefcase size={14} /> Convert to Case
              </button>
            )}
            <button className="btn btn-ghost" onClick={() => setShowEdit(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
      <AddProviderModal
        isOpen={showAddProvider}
        onClose={() => setShowAddProvider(false)}
        initialName={addProviderName}
        providerTypes={providerTypes}
        specialties={specialties}
        onCreated={(newProv) => {
          setProviders(prev => [...prev, newProv]);
          setForm(f => ({ ...f, referred_by: newProv.name }));
          setRefSearch('');
        }}
      />
      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </>
  );
}